import { useEffect, useState, useCallback } from "react";
import { client } from "@/lib/api";

// Locale -> currency defaults. Falls back to USD.
const LOCALE_TO_CURRENCY = [
  { match: /^en-US/i, code: "USD" },
  { match: /^nb|^nn|^no|-NO$/i, code: "NOK" },
  // Any other European locale defaults to EUR
  { match: /^(de|fr|es|it|nl|pt|el|fi|sv|da|pl|cs|sk|hu|ro|bg|hr|sl|lt|lv|et|ga|mt)/i, code: "EUR" },
];

export function detectLocalCurrency() {
  if (typeof navigator === "undefined") return "USD";
  const langs = [navigator.language, ...(navigator.languages || [])].filter(Boolean);
  for (const lang of langs) {
    for (const rule of LOCALE_TO_CURRENCY) {
      if (rule.match.test(lang)) return rule.code;
    }
  }
  return "USD";
}

// Convert amount from source currency to target using EUR-based reference rates.
// rates map is { EUR: 1.0, USD: 1.09, NOK: 11.55, ... }
export function convertAmount(amount, from, to, rates) {
  if (from === to) return amount;
  if (!rates || !rates[from] || !rates[to]) return amount;
  if (amount === null || amount === undefined || amount === "" || isNaN(amount)) return amount;
  const eur = Number(amount) / rates[from];
  return eur * rates[to];
}

// Monetary keys inside `inputs` that should be converted when currency changes.
const MONEY_INPUT_KEYS = ["budget", "aov", "ltv", "risk_reduction_value"];
const MONEY_COST_KEYS = ["writing", "agency", "distribution", "design", "paid_amplification", "tracking"];
// Benchmark keys that are monetary (per-unit amounts, not ratios)
const MONEY_BENCHMARK_KEYS = ["cpc", "cost_per_lead", "avg_backlink_value", "cpm"];

export function convertInputs(inputs, from, to, rates, { round = 0 } = {}) {
  const factor = (v) => {
    const c = convertAmount(v, from, to, rates);
    if (typeof c !== "number" || isNaN(c)) return v;
    return round === 0 ? Math.round(c) : Number(c.toFixed(round));
  };
  const next = { ...inputs };
  for (const k of MONEY_INPUT_KEYS) if (k in next && next[k] !== "" && next[k] != null) next[k] = factor(next[k]);
  if (next.costs) {
    const nc = { ...next.costs };
    for (const k of MONEY_COST_KEYS) if (k in nc && nc[k] !== "" && nc[k] != null) nc[k] = factor(nc[k]);
    next.costs = nc;
  }
  return next;
}

export function convertBenchmarkOverrides(benchmarks, from, to, rates) {
  if (!benchmarks) return benchmarks;
  const next = { ...benchmarks };
  for (const k of MONEY_BENCHMARK_KEYS) {
    if (k in next && next[k] !== "" && next[k] != null) {
      const c = convertAmount(next[k], from, to, rates);
      if (typeof c === "number" && !isNaN(c)) next[k] = Number(c.toFixed(2));
    }
  }
  return next;
}

export function useCurrencyRates() {
  const [rates, setRates] = useState({ EUR: 1, USD: 1.09, NOK: 11.55 });
  const [meta, setMeta] = useState({ as_of: null, source: null });
  useEffect(() => {
    client.get("/currency/rates").then((r) => {
      if (r?.data?.rates) setRates(r.data.rates);
      setMeta({ as_of: r?.data?.as_of, source: r?.data?.source });
    }).catch(() => {});
  }, []);
  const format = useCallback((asOf) => {
    if (!asOf) return "";
    try {
      return new Date(asOf).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
    } catch { return asOf; }
  }, []);
  return { rates, asOf: meta.as_of, source: meta.source, formatDate: format };
}
